// src/components/GameStatusMessage.js
import React, { useMemo, useEffect, useState } from 'react';
import { getMedalThresholds } from '../utils/medalUtils';

const GameStatusMessage = ({ gameStatus, playerLength, communityBestLength, areMedalsLocked }) => {
    const [animate, setAnimate] = useState(false);

    // Trigger animation when playerLength changes
    useEffect(() => {
        setAnimate(true);
        const timer = setTimeout(() => setAnimate(false), 500);
        return () => clearTimeout(timer);
    }, [playerLength]);

    const statusContent = useMemo(() => {
        if (gameStatus === 'won') {
             return {
                text: "Victory! You've matched the Community Best.",
                subtext: "Find a longer word to set a new record, or check back for tomorrow's puzzle.",
                className: "text-foreground dark:text-dark-foreground"
            };
        }
        if (gameStatus === 'lost') {
            return {
                text: "Path revealed. Try again with the next puzzle tomorrow!",
                className: "text-foreground dark:text-dark-foreground font-semibold"
            };
        }

        // Check if medals are locked (Word Revealed)
        if (areMedalsLocked) {
             return {
                text: "Medals Locked. Playing for fun!",
                subtext: "You can no longer earn medals or submit a community best for today.",
                className: "text-gray-500 dark:text-gray-400 font-medium"
            };
        }

        // Playing
        // Note: With new logic, even if communityBestLength is 0, we have static thresholds.
        // But the message "Be the first to set the Community Best" is still valid if CB is 0.
        if (!communityBestLength) {
             return {
                text: "Be the first to set the Community Best!",
                className: "text-gray-500 dark:text-gray-400"
            };
        }

        if (playerLength >= communityBestLength) {
            return {
                text: "Community Champion! Can you find an even longer word?",
                className: "text-foreground dark:text-dark-foreground font-semibold"
            };
        }

        const t = getMedalThresholds(communityBestLength);
        const bronze = t.bronze;
        const silver = t.silver;
        const gold = t.gold;
        const platinum = t.platinum;

        let nextMilestone = '';
        let distance = 0;
        let milestoneName = '';

        if (playerLength < bronze) {
            nextMilestone = bronze;
            milestoneName = 'Bronze';
        } else if (playerLength < silver) {
            nextMilestone = silver;
            milestoneName = 'Silver';
        } else if (playerLength < gold) {
            nextMilestone = gold;
            milestoneName = 'Gold';
        } else {
            nextMilestone = platinum;
            milestoneName = 'Community Best';
        }

        distance = nextMilestone - playerLength;
        const letterText = distance === 1 ? 'letter' : 'letters';

        // Add some variety based on distance
        let text = '';
        if (distance === 1) {
            text = `Only 1 more letter for ${milestoneName}!`;
        } else if (distance <= 3) {
            text = `Close! ${distance} ${letterText} to reach ${milestoneName}.`;
        } else {
            text = `${distance} ${letterText} to reach ${milestoneName}.`;
        }

        return {
            text: text,
            className: "text-gray-500 dark:text-gray-400 font-medium"
        };

    }, [gameStatus, playerLength, communityBestLength, areMedalsLocked]);

    return (
        <div className={`text-center my-2 transition-transform duration-300 ${animate ? 'scale-105' : 'scale-100'}`}>
            <p className={`text-lg ${statusContent.className}`}>{statusContent.text}</p>
            {statusContent.subtext && <p className="text-sm text-gray-500 dark:text-gray-400">{statusContent.subtext}</p>}
        </div>
    );
};

export default GameStatusMessage;
